import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Users, Target, MapPin } from "lucide-react";

const MORGANTOWN_SCENES = [
  {
    src: "/assets/wvu-woodburn.jpg",
    alt: "Woodburn Hall, West Virginia University",
    caption: "West Virginia University · Woodburn Hall, Morgantown, WV",
    credit: "(NNC is not affiliated with West Virginia University.)",
  },
  {
    src: "/assets/coopers-rock.jpg",
    alt: "View from Cooper's Rock, Morgantown, WV",
    caption: "View from Cooper's Rock, Morgantown, WV",
    credit: "Photograph by Npatchett · Public Domain via Wikimedia Commons.",
  },
  {
    src: "/assets/point-state-park.webp",
    alt: "Point State Park at the confluence of the Monongahela, Ohio, and Allegheny Rivers, Pittsburgh, PA",
    caption:
      "Point State Park — Confluence of the Monongahela, Ohio, and Allegheny Rivers, Pittsburgh, PA",
    credit: "Photograph courtesy of the National Park Service.",
  },
  {
    src: "/assets/wvu-mountaineer.jpg",
    alt: "WVU Mountaineer Mascot",
    caption: "Mary Roush — 2022–23 WVU Mountaineer Mascot",
    credit: "Photograph courtesy of West Virginia University.",
  },
];

const PRINCIPLES = [
  {
    icon: ShieldCheck,
    title: "Integrity, on the record.",
    subtitle:
      "We do the work the way federal funders, auditors, and boards expect — documented, defensible, and complete.",
  },
  {
    icon: Target,
    title: "Mission First — Everything Starts with Your Vision.",
    subtitle:
      "We help you articulate the big picture, then we translate your vision at every level down to the persuasive statements that will best reflect your ideas to the world.",
  },
  {
    icon: Users,
    title: "Partners for the Long Term.",
    subtitle:
      "We're here to guide you through the entire creation and funding process.",
  },
];

const About = () => {
  const [mIdx, setMIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setMIdx((i) => (i + 1) % MORGANTOWN_SCENES.length);
    }, 15000);
    return () => clearInterval(id);
  }, []);
  const mScene = MORGANTOWN_SCENES[mIdx];

  return (
    <main data-testid="about-page">
      {/* HERO */}
      <section className="bg-white border-b border-[#E5E7EB]" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 nnc-fade-up">
              <div className="flex items-center gap-3 mb-5">
                <span className="nnc-gold-rule" />
                <span className="nnc-overline">About the Firm</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight nnc-text leading-[1.05]">
                A consulting firm built around <span className="border-b-4 nnc-gold-border pb-1">mission</span>.
              </h1>
              <p className="mt-8 text-lg leading-relaxed nnc-text max-w-2xl">
                Nonprofit Navigation Consulting, LLC (&ldquo;NNC&rdquo;) was
                formed to help organizations navigate the complex terrain
                between mission and durable impact — through structure,
                strategy, funding, contracting, and disciplined execution. We
                work with nonprofits, government contractors, municipalities,
                universities, school districts, and the coalitions that bring
                them together.
              </p>
            </div>
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <img
                src="/assets/nnc-logo.svg"
                alt="NNC Medallion"
                className="w-64 h-64 lg:w-80 lg:h-80"
                data-testid="about-medallion"
              />
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="bg-white" data-testid="about-principles">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="flex items-center gap-3 mb-4">
            <span className="nnc-gold-rule" />
            <span className="nnc-overline">Operating Principles</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight nnc-text max-w-3xl">
            Guiding Mission, Impact Driven.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {PRINCIPLES.map((p, i) => (
              <div key={p.title} className="bg-white p-10" data-testid={`principle-${i}`}>
                <p.icon className="h-8 w-8 nnc-gold-text mb-6" strokeWidth={2} />
                <h3 className="text-xl font-bold nnc-text">{p.title}</h3>
                {p.subtitle && (
                  <p className="mt-3 text-base font-bold leading-snug nnc-text">
                    {p.subtitle}
                  </p>
                )}
                {p.body && (
                  <p className="mt-3 text-sm leading-relaxed nnc-text">{p.body}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE DO IN ONE SCREEN */}
      <section className="nnc-ceramic-soft-bg border-t border-b border-[#E5E7EB]" data-testid="about-summary">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="nnc-overline mb-4">What We Do</div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight nnc-text">
                From formation to federal closeout — and every step between.
              </h2>
              <Link to="/services" className="nnc-btn mt-8" data-testid="about-cta-services">
                See the Service Directory <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 nnc-text">
                {[
                  "Nonprofit formation & governance",
                  "Strategic planning",
                  "Grant research & writing",
                  "Federal grants administration",
                  "Program evaluation",
                  "Fundraising strategy",
                  "Capacity building",
                  "Crisis management",
                  "Business structure & plans",
                  "Grant project management",
                  "Subject matter expert consulting (housing, community & economic development, workforce, brownfields, public infrastructure, education, and more)",
                  "Government contracting — capture & proposals",
                  "Federal registrations & small business certifications",
                  "Contract administration & FAR/DFARS compliance",
                ].map((s, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm leading-relaxed">
                    <span className="mt-2 inline-block w-2 h-2 nnc-gold-bg shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* TEAM PLACEHOLDER */}
      <section className="bg-white" data-testid="about-team">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="flex items-center gap-3 mb-4">
            <span className="nnc-gold-rule" />
            <span className="nnc-overline">Our Team</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight nnc-text max-w-3xl">
            Expert Consultants. Proven Results.
          </h2>
          <p className="mt-6 text-base leading-relaxed nnc-text max-w-3xl">
            Detailed team bios and credentials will be added as content is
            finalized. Our practice draws on senior consultants with experience
            in nonprofit leadership, federal grant administration, community
            development, housing, workforce, and public sector finance.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sheila Coleman-Castells — Founding Principal */}
            <article
              className="nnc-card p-0 md:col-span-2 lg:col-span-1 overflow-hidden"
              data-testid="team-sheila-coleman-castells"
            >
              <div className="aspect-[4/5] w-full overflow-hidden bg-[#FBF2DE]">
                <img
                  src="/assets/sheila-coleman-castells.jpg"
                  alt="Sheila Coleman-Castells, Founding Principal"
                  className="w-full h-full object-cover object-center"
                  data-testid="team-sheila-photo"
                />
              </div>
              <div className="p-8">
                <div className="nnc-card-accent" />
                <h3 className="mt-4 text-xl font-bold nnc-text leading-tight">
                  Sheila Coleman-Castells
                </h3>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] nnc-gold-text">
                  Co-Partner
                </p>
                <p className="mt-4 text-sm leading-relaxed nnc-text">
                  Sheila serves NNC as Co-Partner and works as a{" "}
                  <strong>Fractional Executive and Strategic Partnerships
                  Strategist</strong>. She partners with educational
                  institutions, workforce development organizations,
                  nonprofits, government agencies, trade associations, and
                  community initiatives to strengthen organizational
                  capacity, develop strategic partnerships, secure funding,
                  and build the systems that create economic mobility and
                  advancement.
                </p>
                <p className="mt-3 text-sm leading-relaxed nnc-text">
                  Over more than <strong>25 years</strong>, she has led
                  initiatives in workforce development, higher education,
                  industry training and apprenticeship systems, health and
                  disability policy, bilingual and ESL education,
                  faith-based community and economic development,
                  energy-efficient and renewable-energy housing, and
                  public-private partnerships at the federal, state, and
                  local levels. She has worked extensively with
                  linguistically and culturally diverse populations in both
                  domestic and international settings, helping organizations
                  develop inclusive and impactful programs.
                </p>
                <p className="mt-4 text-sm font-bold nnc-text">
                  Her work includes:
                </p>
                <ul className="mt-2 space-y-2">
                  {[
                    "Designing and managing funding strategies and advocacy for nonprofits, education, workforce initiatives, and government partners.",
                    "Developing partnerships among employers, educational institutions, labor organizations, government agencies, faith-based organizations, and community stakeholders.",
                    "Advising organizations on workforce and apprenticeship system development, strategic planning, and organizational transformation.",
                    "Supporting colleges, universities, school districts, and workforce agencies in aligning programs with labor market goals.",
                    "Conducting program evaluation, compliance review, and implementation analysis for federally and privately funded initiatives.",
                    "Supporting multilingual, multicultural, immigrant, and underserved communities through inclusive workforce development strategies.",
                  ].map((s, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-sm leading-relaxed nnc-text"
                    >
                      <span className="mt-2 inline-block w-1.5 h-1.5 nnc-gold-bg shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-sm leading-relaxed nnc-text">
                  Across her career she has helped clients secure{" "}
                  <strong>more than $75 million</strong> in federal, state,
                  and private funding. Her background includes leadership
                  roles within the <strong>U.S. Departments of Education and
                  Labor</strong>, executive nonprofit leadership,
                  international university teaching, and consulting work
                  throughout the United States and abroad.
                </p>
                <p className="mt-3 text-sm leading-relaxed nnc-text">
                  Sheila is <strong>Founder and first President of TESOL
                  Arabia</strong> and served as Professor of English at{" "}
                  <strong>United Arab Emirates University</strong>. She has
                  taught as Professor of English-as-a-Second Language
                  Practice and Education Research at the University of
                  Virginia, University of Maryland-College Park, American
                  University, The George Washington University, West
                  Virginia University, Howard University, Frostburg State
                  University, and Northern Virginia Community College
                  (Annandale and Loudoun campuses). Earlier in her career,
                  she served as a <strong>Resource Teacher of Bilingual/ESL
                  Education and Special Education</strong> for the{" "}
                  <strong>District of Columbia Public Schools</strong> and{" "}
                  <strong>Fairfax County Public Schools</strong>.
                </p>
                <p className="mt-3 text-sm leading-relaxed nnc-text">
                  Studied at the University of Virginia (Ph.D., Education
                  Research, ABD), George Mason University (M.Ed., Secondary
                  Education — Bilingual/Multicultural Education, with TESL
                  Certificate; B.A., French Language &amp; Literature), and
                  Boston College (M.A., Theology &amp; Ministry, in
                  process), with additional study at the Université Laval
                  (Québec) and the Université de Neuchâtel (Switzerland).
                  Fluent in French and proficient in Spanish. Certified BPI
                  (BA-T) and HERS Rater (RESNET).
                </p>
                <p className="mt-3 text-sm leading-relaxed nnc-text">
                  Board Member, West Virginia Botanic Garden. Recognized as
                  a <em>Woman of Resilience</em> on Black Policy Day, 2025,
                  at the West Virginia State Capitol.{" "}
                  <strong>Mezzo Soprano and Specialist in Sacred Music.</strong>
                </p>
              </div>
            </article>

            {/* Dr. Bailey Bridgewater — Co-Partner */}
            <article
              className="nnc-card p-0 md:col-span-2 lg:col-span-1 overflow-hidden"
              data-testid="team-bailey-bridgewater"
            >
              <div className="aspect-[4/5] w-full overflow-hidden bg-[#FBF2DE]">
                <img
                  src="/assets/bailey-bridgewater.jpg"
                  alt="Dr. Bailey Bridgewater, Co-Partner"
                  className="w-full h-full object-cover object-center"
                  data-testid="team-bailey-photo"
                />
              </div>
              <div className="p-8">
                <div className="nnc-card-accent" />
                <h3 className="mt-4 text-xl font-bold nnc-text leading-tight">
                  Dr. Bailey Bridgewater
                </h3>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] nnc-gold-text">
                  Co-Partner
                </p>
                <p className="mt-4 text-sm leading-relaxed nnc-text">
                  Bailey&apos;s journey started in academia. She began her
                  career teaching English Composition to college students in
                  Pennsylvania, eventually moving to Indiana for an
                  administrative role that saw her lead disability services,
                  a writing center, tutoring resources, and mentoring. Her
                  passion for helping others strengthen their writing skills
                  led her to create a writing group for graduate students,
                  as well as annual week-long writing retreats for students
                  completing theses and dissertations. These events combined
                  the natural settings of Indiana State Parks, guided hikes,
                  workshops on editing, social activities, and intensive
                  writing support — all geared toward giving students the
                  tools to complete a large-scale piece of research writing.
                </p>
                <p className="mt-3 text-sm leading-relaxed nnc-text">
                  Given the success of these support programs, Bailey was
                  tasked with creating a Writing Across the Curriculum
                  program for the university. She was simultaneously tasked
                  with leading the general education program&apos;s
                  evaluation and assessment efforts — creating rubrics and a
                  document analysis strategy, teaching faculty members to
                  assess written work, analyzing collected data, and
                  reporting to university leadership. She trained on
                  assessment with the Higher Learning Commission and
                  regularly scored work for the organization.
                </p>
                <p className="mt-3 text-sm leading-relaxed nnc-text">
                  While performing her role as Executive Director within the
                  university&apos;s Student Success division, Bailey earned
                  her <strong>Ph.D. in Higher Education Administration</strong>,
                  completing a dissertation on multilingualism and writing
                  support in South African writing centers. During this time
                  she began to learn academic grant writing and was tasked
                  with leading university efforts to apply for a U.S.
                  Department of Education Strengthening Institutions grant
                  to create a mentoring center. The application received a
                  perfect score and was funded at <strong>over $2 million</strong>.
                  Bailey then assisted in editing and creating the
                  evaluation plan for a Lilly Foundation grant funded over
                  three phases, securing a total of <strong>$9 million</strong>.
                </p>
                <p className="mt-3 text-sm leading-relaxed nnc-text">
                  As Bailey&apos;s experience with grants and PI-ship
                  evolved, she came to believe she could do the most good by
                  focusing on university and nonprofit fundraising as an
                  independent consultant. To that end, her first company,{" "}
                  <em>Bridgewater Writing and Grant Services</em>, was
                  founded in 2021. As CEO and grant manager, she has helped
                  nonprofits, small businesses, Tribes, and universities
                  secure <strong>more than $48 million</strong> in grants
                  and government contracts. Her wins include federal
                  agencies such as DOT, DOE, USDA, NSF, and SAMHSA, as well
                  as state and provincial grants, local awards, and private
                  foundations.
                </p>
                <p className="mt-3 text-sm leading-relaxed nnc-text">
                  Bailey is passionate about helping organizations tell
                  their stories and design bounded, fundable projects that
                  serve their communities. She enjoys building evaluation
                  and assessment plans that allow funders to see — with
                  full transparency — the good their money is doing,
                  through solid and digestible data and reporting; she also
                  serves, on occasion, as external evaluator for academic
                  projects. Grant writing may be technical, but at the end
                  of the day it is about helping communities — and that is
                  why Bailey has dedicated herself to this line of work.
                </p>
                <p className="mt-3 text-sm leading-relaxed nnc-text">
                  Bailey is a member of the{" "}
                  <strong>Grant Professionals Association</strong>.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section className="nnc-bg" data-testid="about-location">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="inline-block w-12 h-[2px] nnc-gold-bg" />
                <span className="text-xs font-bold uppercase tracking-[0.28em] nnc-gold-text">
                  Where We Are
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                Morgantown, West Virginia.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-white/90 max-w-xl">
                Home of West Virginia University and a regional crossroads
                1.5 hours from Pittsburgh, PA — anchored by a love of the
                Mountaineers and the beautiful Appalachian Mountains.{" "}
                <strong>Rooted here, but working everywhere.</strong>
              </p>
              <div className="mt-8 flex items-center gap-3 text-white">
                <MapPin className="h-5 w-5 nnc-gold-text" strokeWidth={2.5} />
                <span className="font-bold tracking-wide">
                  Morgantown, WV · United States
                </span>
              </div>
            </div>
            <div className="relative" data-testid="about-morgantown-slideshow">
              <div className="aspect-[4/3] w-full overflow-hidden border-4 nnc-gold-border bg-black relative">
                {MORGANTOWN_SCENES.map((s, i) => (
                  <img
                    key={s.src}
                    src={s.src}
                    alt={s.alt}
                    className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out"
                    style={{ opacity: i === mIdx ? 1 : 0 }}
                    data-testid={`about-morgantown-image-${i}`}
                  />
                ))}
                <div
                  className="absolute left-0 right-0 bottom-0 px-5 py-3 backdrop-blur-sm pointer-events-none"
                  style={{ backgroundColor: "rgba(0, 32, 91, 0.88)" }}
                  data-testid="about-morgantown-caption"
                >
                  <p className="text-xs sm:text-sm font-bold tracking-wide text-white leading-snug">
                    {mScene.caption}
                  </p>
                  {mScene.credit && (
                    <p className="text-[10px] sm:text-[11px] mt-1 italic text-white/80 leading-snug">
                      {mScene.credit}
                    </p>
                  )}
                </div>
              </div>
              <div
                className="mt-4 flex items-center justify-center gap-2"
                data-testid="about-morgantown-dots"
              >
                {MORGANTOWN_SCENES.map((s, i) => (
                  <button
                    key={s.src}
                    type="button"
                    onClick={() => setMIdx(i)}
                    aria-label={`Show scene ${i + 1}`}
                    data-testid={`about-morgantown-dot-${i}`}
                    className={`h-2 transition-all ${
                      i === mIdx
                        ? "w-8 nnc-gold-bg"
                        : "w-2 bg-white/30 hover:bg-[#FCB814]"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
